import { ref, computed, watch, markRaw } from "vue";
import { defineStore } from "pinia";
import type { PhotoDocument } from "@kekkle/shared/types/documents/photo-document/photo-document";
import {
  subscribeToAllPhotos,
  subscribeToPhotoChanges,
  countPhotos,
  createPhotoId,
  addPhoto as addPhotoService,
  updatePhoto as updatePhotoService,
  deletePhoto as deletePhotoService,
  reportPhoto as reportPhotoService,
} from "@/helpers/firestore/PhotoService";
import { usePhotoFilterStore } from "@/stores/modules/photos/photo-filter";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useDeltaCollection } from "@/composables/useDeltaCollection";
import { deletePicture } from "@/helpers/firestorage/PictureService";
import { getPhotoFromFirestorage } from "@/helpers/firestorage/PhotoPictureService";
import { onCacheInvalidated } from "@/helpers/cache/image-cache";
import { CacheTypes } from "@/enums/cache-types";
import { ImageSizes } from "@/enums/images-sizes";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";
import type { FirestoreError } from "firebase/firestore";
import type { CacheSnapshot } from "vue-virtual-scroller";

export type PhotoMemory = {
  yearsAgo: number;
  date: Date;
  count: number;
};

/** A loaded SD image blob plus its object URL, kept in memory across remounts. */
export type StoredPhotoImage = {
  blob: Blob;
  url: string;
};

/**
 * Photos store for managing photo documents and memories per group.
 * Delta-syncs with Firestore (see useDeltaCollection): one full read per
 * device, after that only photos whose updated_at changed.
 * Date filtering is done client-side via computed properties.
 * Only one active listener at a time (current group).
 */
export const usePhotosStore = defineStore("photos", () => {
  const userStore = useUserStore();
  const filterStore = usePhotoFilterStore();

  const fetchError = ref<string | null>(null);

  /* State - organized by group ID */
  const sync = useDeltaCollection<PhotoDocument>({
    cacheName: "photos",
    subscribeToAll: subscribeToAllPhotos,
    subscribeToChanges: subscribeToPhotoChanges,
    countFromServer: countPhotos,
    onError: (error) => {
      fetchError.value = handleFirestoreException({ e: error });
    },
  });
  const {
    documentsByGroup: photosByGroup,
    loadingByGroup,
    isStaleByGroup,
  } = sync;

  /**
   * In-memory cache of loaded image blobs, keyed by `${groupId}:${photoId}:${format}`.
   * Entries are markRaw'd so the Blob is never wrapped in a reactive proxy;
   * only key insertion into the record is tracked, which is the single signal
   * consumers need to swap from blurhash placeholder to the real image.
   */
  const imagesById = ref<Record<string, StoredPhotoImage>>({});

  /* De-duplicates concurrent loads of the same photo+format (non-reactive). */
  const inFlightImageLoads = new Map<string, Promise<void>>();

  const selectedPhoto = ref<PhotoDocument | Record<string, never>>({});
  const scrollIndex = ref(0);
  const scrollOffset = ref(0);
  let scrollerCache: CacheSnapshot | null = null;

  /* Computed / Getters - for active group */
  const activeGroupId = computed(() => userStore.activeGroup);

  /** All photos for the active group (unfiltered, from onSnapshot) */
  const allPhotos = computed(
    () =>
      (activeGroupId.value && photosByGroup.value[activeGroupId.value]) || [],
  );

  /** Filtered photos - what the view consumes */
  const photos = computed(() => {
    const params = filterStore.getFilterParams();
    let result = allPhotos.value;

    if (params.date) {
      const filterDate = params.date;
      result = result.filter((p) => isSameDay(p.timestamp, filterDate));
    }

    return result;
  });

  const loadingInitialPhotos = computed(() => {
    if (!activeGroupId.value) return false;
    return loadingByGroup.value[activeGroupId.value] ?? false;
  });

  const isStale = computed(() => {
    if (!activeGroupId.value) return false;
    return isStaleByGroup.value[activeGroupId.value] ?? false;
  });

  /** Photos from this day in previous years (derived from allPhotos) */
  const photoMemories = computed(() => {
    if (allPhotos.value.length === 0) return [];

    const oldestPhoto = allPhotos.value[allPhotos.value.length - 1]!;
    const today = new Date();
    const currentYear = today.getFullYear();
    const maxYearsBack = Math.min(
      10,
      currentYear - oldestPhoto.timestamp.getFullYear(),
    );

    const memories: PhotoMemory[] = [];
    for (let i = 1; i <= maxYearsBack; i++) {
      const dateToCheck = new Date(today);
      dateToCheck.setFullYear(currentYear - i);

      const count = allPhotos.value.filter((p) =>
        isSameDay(p.timestamp, dateToCheck),
      ).length;

      if (count > 0) {
        memories.push({ yearsAgo: i, date: dateToCheck, count });
      }
    }
    return memories;
  });

  const hasSelectedPhoto = computed(
    () => Object.keys(selectedPhoto.value).length > 0,
  );

  /* Helper functions */
  function isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  /* Listener lifecycle */
  function subscribe(groupId: string): void {
    if (sync.subscribe(groupId)) {
      fetchError.value = null;
    }
  }

  function unsubscribe(): void {
    sync.unsubscribe();
  }

  /* Image blob cache + queue */

  function imageCacheKey(
    groupId: string,
    photoId: string,
    format: ImageSizes,
  ): string {
    return `${groupId}:${photoId}:${format}`;
  }

  /** Get an already-loaded image for a photo at the given size, if present. */
  function getPhotoImage(
    groupId: string,
    photoId: string,
    format: ImageSizes = ImageSizes.SD,
  ): StoredPhotoImage | undefined {
    return imagesById.value[imageCacheKey(groupId, photoId, format)];
  }

  async function fetchAndStoreImage(
    groupId: string,
    photo: PhotoDocument,
    format: ImageSizes,
  ): Promise<void> {
    if (!groupId) return;

    try {
      // The service transparently falls back to the original image when the
      // requested size variant hasn't been generated yet.
      const result = await getPhotoFromFirestorage(
        groupId,
        photo._id,
        photo.picture_url,
        format,
      );
      imagesById.value[imageCacheKey(groupId, photo._id, format)] = markRaw({
        blob: result.blob,
        url: result.url,
      });
    } catch (error) {
      console.error("Error loading photo image:", error);
    }
  }

  /**
   * Ensure an image for a photo (at the given size) is loaded into the store.
   * No-op if already cached; de-duplicates concurrent requests for the same
   * photo+size. Callers (grid/photo components) already gate this on viewport
   * visibility, so no separate concurrency limit is needed.
   */
  function loadPhotoImage(
    groupId: string,
    photo: PhotoDocument,
    format: ImageSizes = ImageSizes.SD,
  ): Promise<void> {
    const key = imageCacheKey(groupId, photo._id, format);
    if (imagesById.value[key]) return Promise.resolve();

    const existing = inFlightImageLoads.get(key);
    if (existing) return existing;

    const promise = fetchAndStoreImage(groupId, photo, format).finally(() =>
      inFlightImageLoads.delete(key),
    );
    inFlightImageLoads.set(key, promise);
    return promise;
  }

  /**
   * Drop every cached size of a single photo from the in-memory store and
   * revoke its object URLs. Returns the sizes that were evicted so the caller
   * can re-fetch exactly what was in use. loadPhotoImage short-circuits on a
   * present cache entry, so eviction must happen before any re-download.
   */
  function evictPhotoImages(groupId: string, photoId: string): ImageSizes[] {
    const prefix = `${groupId}:${photoId}:`;
    const evictedFormats: ImageSizes[] = [];
    for (const key of Object.keys(imagesById.value)) {
      if (!key.startsWith(prefix)) continue;
      URL.revokeObjectURL(imagesById.value[key]!.url);
      delete imagesById.value[key];
      inFlightImageLoads.delete(key);
      evictedFormats.push(key.slice(prefix.length) as ImageSizes);
    }
    return evictedFormats;
  }

  /**
   * Keep the in-memory image cache coherent with the IndexedDB layer. When
   * image-cache.ts detects an MD5 mismatch it clears IndexedDB and fires this
   * callback with the base pattern `photo:${groupId}:${photoId}` (no size
   * suffix). Without this, a re-uploaded photo would keep showing the old
   * bitmap until logout, since our cache sits in front of IndexedDB.
   */
  onCacheInvalidated((keyPattern) => {
    if (keyPattern === "*") {
      revokeImageUrls();
      return;
    }
    const prefix = `${CacheTypes.Photo}:`;
    if (!keyPattern.startsWith(prefix)) return;

    // "photo:groupId:photoId" -> "groupId:photoId"
    const [groupId, photoId] = keyPattern.slice(prefix.length).split(":");
    if (!groupId || !photoId) return;

    const evictedFormats = evictPhotoImages(groupId, photoId);
    if (evictedFormats.length === 0) return;

    // Re-download the fresh image for every size we had cached. The visibility
    // watcher won't re-fire for a photo that's already on screen, so we must
    // re-request here or it would keep showing the stale bitmap. Scoped to the
    // active group, since fetchAndStoreImage always fetches against it.
    if (groupId !== activeGroupId.value) return;
    const photo = photosByGroup.value[groupId]?.find((p) => p._id === photoId);
    if (!photo) return;
    for (const format of evictedFormats) {
      loadPhotoImage(groupId, photo, format);
    }
  });

  /* Actions */
  function setSelectedPhoto(photo: PhotoDocument): void {
    selectedPhoto.value = photo;
  }

  function revokeImageUrls(): void {
    // Release object URLs before dropping the image cache to avoid leaks.
    for (const image of Object.values(imagesById.value)) {
      URL.revokeObjectURL(image.url);
    }
    imagesById.value = {};
    inFlightImageLoads.clear();
  }

  function reset(): void {
    sync.reset();
    revokeImageUrls();
  }

  /** Subscribe to photos for the active group */
  function subscribeToPhotos(): void {
    const groupId = activeGroupId.value;
    if (!groupId) return;
    subscribe(groupId);
  }

  async function addPhoto(data: Omit<PhotoDocument, "_id">): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      const photo: PhotoDocument = { ...data, _id: createPhotoId(groupId) };
      await sync.writeLocally(groupId, photo, () =>
        addPhotoService(groupId, photo),
      );
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function updatePhoto(
    photoId: string,
    data: Partial<Omit<PhotoDocument, "_id">>,
  ): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      const existing = photosByGroup.value[groupId]?.find(
        (photo) => photo._id === photoId,
      );
      const write = () => updatePhotoService(groupId, photoId, data);
      await (existing
        ? sync.writeLocally(groupId, { ...existing, ...data }, write)
        : write());

      // Update selected photo if it's the one being edited
      if (selectedPhoto.value._id === photoId) {
        const current = selectedPhoto.value as PhotoDocument;
        Object.assign(current, data);
      }
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function deletePhoto(photoId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    // Find the photo to get its absolute_picture_url before deleting
    const currentPhotos = photosByGroup.value[groupId] ?? [];
    const photoToDelete = currentPhotos.find((photo) => photo._id === photoId);

    try {
      // Delete from Firestore
      await deletePhotoService(groupId, photoId);
      // Deletes never reach the delta listener; drop it locally so the
      // next count check does not trigger a full resync.
      sync.removeLocally(groupId, photoId);

      // Delete images from Storage and clear cache (if photo was found)
      if (photoToDelete?.absolute_picture_url) {
        await deletePicture({
          cacheType: CacheTypes.Photo,
          cacheId: photoId,
          basePath: photoToDelete.absolute_picture_url,
        });
      }

      // Clear selected photo if it was the deleted one
      if (selectedPhoto.value._id === photoId) {
        selectedPhoto.value = {};
      }
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function reportPhoto(photoId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    const authStore = useAuthStore();
    const reporterId = authStore.authUuid;

    if (!reporterId) {
      throw new Error("User must be authenticated to report photos");
    }

    await reportPhotoService(groupId, photoId, reporterId);
    // Not applied locally: PhotoDocument has no reported_by field yet, so the
    // delta listener shows the report once the server confirms it.
  }

  /* Watch active group changes */
  watch(
    () => activeGroupId.value,
    (newGroupId, oldGroupId) => {
      if (newGroupId && newGroupId !== oldGroupId) {
        subscribe(newGroupId);
      }
    },
  );

  /* Exposed store */
  return {
    // State
    loadingInitialPhotos,
    isStale,
    isStaleByGroup,
    photos,
    allPhotos,
    selectedPhoto,
    fetchError,
    scrollIndex,
    scrollOffset,
    photoMemories,

    // Computed
    hasSelectedPhoto,

    // Actions
    setSelectedPhoto,
    getPhotoImage,
    loadPhotoImage,
    revokeImageUrls,
    reset,
    subscribeToPhotos,
    addPhoto,
    updatePhoto,
    deletePhoto,
    reportPhoto,
    unsubscribe,
    scrollerCache,
    setScrollState: (
      index: number,
      offset: number,
      cache: CacheSnapshot | null,
    ) => {
      scrollIndex.value = index;
      scrollOffset.value = offset;
      scrollerCache = cache;
    },
  };
});
